/**
 * Session held by a partner iframe (MixVoIP cockpit).
 *
 * The cockpit hands us a one-time token in the URL; we exchange it for an Elora
 * session JWT. That JWT is not a B2C token, so MSAL knows nothing about it —
 * apiService falls back to this store before trying MSAL, which is what lets an
 * embedded session call the same APIs as a signed-in user.
 *
 * sessionStorage, deliberately: the session dies with the tab, so closing the
 * cockpit does not leave a usable Elora token behind on a shared machine.
 */

const SESSION_KEY = 'elora_partner_session';

export interface PartnerSession {
  accessToken: string;
  /** Epoch ms. */
  expiresAt: number;
  /** Decoded from the JWT's own claims (see mixvoipService) — never from the URL. */
  userId?: string;
  companyId?: string;
  companyName?: string;
}

export const savePartnerSession = (session: PartnerSession): void => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Private mode / storage disabled: the page still works for this render,
    // it just cannot survive a reload. Never let storage break the session.
  }
};

export const getPartnerSession = (): PartnerSession | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const session = JSON.parse(raw) as PartnerSession;
    if (!session?.accessToken || typeof session.expiresAt !== 'number') {
      return null;
    }
    // Expire slightly early so a request cannot leave with a token that dies
    // in flight.
    if (session.expiresAt - 30_000 <= Date.now()) {
      clearPartnerSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
};

export const clearPartnerSession = (): void => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // nothing to do
  }
};

/** True when this tab is running inside a partner iframe session. */
export const hasPartnerSession = (): boolean => getPartnerSession() !== null;
